import { useContext, useEffect, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { observer } from 'mobx-react-lite';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid2, TextField, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { AuthStoreContext } from '../Root';
import UserStore from '../../stores/user-store';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emailAllowedChars = /^[a-zA-Z0-9.@_\-+!#$%&'*/=?^`{|}~]*$/;

//-------------------------------------------------------------------------------------------------
const apiError = (error: unknown, fallback: string): string => {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
            const message = (axiosError.response.data as any)?.message;
            if (typeof message === 'string') {
                try {
                    const parsed = JSON.parse(message);
                    if (Array.isArray(parsed) && parsed[0]?.message) return parsed[0].message;
                } catch {}
                return message;
            }
            if (Array.isArray(message)) return message[0]?.message ?? fallback;
        } else if (axiosError.message === 'Network Error') return 'Ошибка сети';
    }
    return fallback;
};

//=================================================================================================
function CurrentUser() {
    const { authStore } = useContext(AuthStoreContext);
    const userStore = useMemo(() => new UserStore(), []);

    const [loadError, setLoadError] = useState<string | null>(null);

    const [email, setEmail] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [activeField, setActiveField] = useState<'new' | 'confirm' | null>(null);
    const [passError, setPassError] = useState<string | null>(null);
    const [passSuccess, setPassSuccess] = useState(false);

    useEffect(() => {
        userStore.load(authStore.user!.user_id).catch((e) => {
            setLoadError(apiError(e, 'Ошибка загрузки данных пользователя'));
        });
    }, []);

    useEffect(() => {
        if (userStore.user) {
            setEmail(userStore.user.email ?? '');
        }
    }, [userStore.user]);

    const emailHasInvalidChars = email.length > 0 && !emailAllowedChars.test(email);
    const emailBadFormat = emailTouched && email.length > 0 && !emailRegex.test(email);
    const emailError = emailHasInvalidChars || emailBadFormat;
    const emailHelperText = emailHasInvalidChars ? 'Недопустимый символ' : emailBadFormat ? 'Некорректный email' : '';
    const emailInvalid = emailHasInvalidChars || (email.length > 0 && !emailRegex.test(email));

    const hasChanges = !!userStore.user && email !== (userStore.user.email ?? '');
    const canSave = hasChanges && !emailInvalid && !userStore.loading;

    const handleSave = async () => {
        setSaveError(null);
        try {
            await userStore.edit({ ...userStore.user!, email: email || null });
        } catch (e: any) {
            setSaveError(apiError(e, 'Ошибка сохранения'));
        }
    };

    const confirmError =
        confirmPassword.length > 0 &&
        (activeField === 'confirm' ? !newPassword.startsWith(confirmPassword) : confirmPassword !== newPassword);

    const canSubmit = currentPassword.length > 0 && newPassword.length > 0 && newPassword === confirmPassword;

    const handleChangePassword = async () => {
        setPassError(null);
        setPassSuccess(false);
        try {
            await userStore.changePassword(newPassword, currentPassword);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPassSuccess(true);
        } catch (e: any) {
            setPassError(apiError(e, 'Ошибка смены пароля'));
        }
    };

    if (userStore.loading && !userStore.user) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, bgcolor: 'background.default' }}>
            <Box sx={{ p: 4, maxWidth: 860, mx: 'auto' }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Настройки пользователя
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                    Управление параметрами учётной записи.
                </Typography>

                {loadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {loadError}
                    </Alert>
                )}

                <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <PersonOutlineIcon />
                            <Typography variant="h6" fontWeight="bold">
                                Профиль
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                            Данные учётной записи.
                        </Typography>
                        <Box sx={{ maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" mb={0.5}>
                                    Имя пользователя
                                </Typography>
                                <TextField fullWidth value={authStore.user!.login} disabled size="small" />
                            </Box>
                            <Box>
                                <Typography variant="body2" mb={0.5}>
                                    Email
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={() => setEmailTouched(true)}
                                    size="small"
                                    error={emailError}
                                    helperText={emailHelperText}
                                    disabled={userStore.loading}
                                    autoComplete="email"
                                />
                            </Box>
                        </Box>
                        {saveError && (
                            <Alert severity="error" sx={{ mt: 2, maxWidth: 400 }}>
                                {saveError}
                            </Alert>
                        )}
                        <Button variant="contained" sx={{ mt: 3 }} disabled={!canSave} onClick={handleSave}>
                            Сохранить
                        </Button>
                    </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <LockOutlinedIcon />
                            <Typography variant="h6" fontWeight="bold">
                                Смена пароля
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                            Обновление пароля учётной записи.
                        </Typography>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" mb={0.5}>
                                    Текущий пароль
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    size="small"
                                    autoComplete="current-password"
                                />
                            </Grid2>
                            <Grid2 size={12} />
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" mb={0.5}>
                                    Новый пароль
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    onFocus={() => setActiveField('new')}
                                    onBlur={() => setActiveField(null)}
                                    size="small"
                                    autoComplete="new-password"
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" mb={0.5}>
                                    Повтор нового пароля
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    onFocus={() => setActiveField('confirm')}
                                    onBlur={() => setActiveField(null)}
                                    size="small"
                                    autoComplete="new-password"
                                    error={confirmError}
                                    helperText={confirmError ? 'Пароли не совпадают' : ''}
                                />
                            </Grid2>
                        </Grid2>
                        <Button
                            variant="contained"
                            sx={{ mt: 3 }}
                            disabled={!canSubmit || userStore.loading}
                            onClick={handleChangePassword}
                        >
                            Сменить пароль
                        </Button>
                        {passError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {passError}
                            </Alert>
                        )}
                        {passSuccess && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                                Пароль успешно изменён
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default observer(CurrentUser);
